import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Layout } from './components/layouts/Layout';
import { AuthLayout } from './components/layouts/AuthLayout';
import { CustomerLayout } from './components/layouts/CustomerLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import ToastProvider from './components/ui/toast/ToastProvider';
import i18n from './i18n';

const initialData = window.__INITIAL_DATA__;
const pageName = window.__PAGE_NAME__;
const layoutName = window.__LAYOUT_NAME__ ?? 'default';

const rootElement = document.getElementById('root');

const layouts: Record<string, React.ComponentType<{ children: React.ReactNode }>> = {
  default: Layout,
  auth: AuthLayout,
  customer: CustomerLayout,
};

const pageLoaders: Record<string, () => Promise<React.ComponentType<any>>> = {
  DashboardPage: async () => (await import('./pages/DashboardPage/DashboardPage')).DashboardPage,
  ProfilePage: async () => (await import('./pages/ProfilePage/ProfilePage')).ProfilePage,
  LoginPage: async () => (await import('./pages/LoginPage/LoginPage')).LoginPage,
  OtpPage: async () => (await import('./pages/OtpPage/OtpPage')).OtpPage,
  OAuthCompletePage: async () => (await import('./pages/OAuthCompletePage/OAuthCompletePage')).OAuthCompletePage,
  RegisterPage: async () => (await import('./pages/RegisterPage/RegisterPage')).RegisterPage,
  IndexPage: async () => (await import('./pages/IndexPage/IndexPage')).IndexPage,
  ForgotPasswordPage: async () => (await import('./pages/ForgotPasswordPage/ForgotPasswordPage')).ForgotPasswordPage,
  ResetPasswordPage: async () => (await import('./pages/ResetPasswordPage/ResetPasswordPage')).ResetPasswordPage,
  ToursPage: async () => (await import('./pages/ToursPage/ToursPage')).ToursPage,
  TourDetailsPage: async () => (await import('./pages/TourDetailsPage/TourDetailsPage')).TourDetailsPage,
  TourBookingPage: async () => (await import('./pages/TourBookingPage/TourBookingPage')).TourBookingPage,
  BookingDetailsPage: async () => (await import('./pages/BookingDetailsPage/BookingDetailsPage')).BookingDetailsPage,
  MyDashboard: async () => (await import('./pages/MyDashboard/MyDashboard')).MyDashboard,
  SelectPaymentPage: async () => (await import('./pages/SelectPaymentPage/SelectPaymentPage')).SelectPaymentPage,
  BankTransferDetailsPage: async () => (await import('./pages/SelectPaymentPage/BankTransferDetailsPage')).BankTransferDetailsPage,
  PrivacyPolicyPage: async () => (await import('./pages/PrivacyPolicyPage/PrivacyPolicyPage')).PrivacyPolicyPage,
  TermsOfUsePage: async () => (await import('./pages/TermsOfUsePage/TermsOfUsePage')).TermsOfUsePage,
  AboutUsPage: async () => (await import('./pages/AboutUsPage/AboutUsPage')).AboutUsPage,
  NotFoundPage: async () => (await import('./pages/NotFoundPage/NotFoundPage')).NotFoundPage,
};

async function bootstrap() {
  if (!rootElement) {
    console.error('Không tìm thấy element #root để hydrate.');
    return;
  }

  const loader = pageLoaders[pageName];
  if (!loader) {
    console.error(`Không tìm thấy component cho trang "${pageName}" để hydrate.`);
    return;
  }

  const PageComponent = await loader();
  const LayoutComponent = layouts[layoutName] ?? Layout;
  hydrateRoot(
    rootElement,
    <ErrorBoundary>
      <ToastProvider>
        <LayoutComponent>
          <PageComponent initialData={initialData} />
        </LayoutComponent>
      </ToastProvider>
    </ErrorBoundary>
  );

  try {
    const stored = window.localStorage.getItem('koitour_lang');
    const next = stored === 'en' || stored === 'kr' || stored === 'vn' ? stored : null;
    if (next && i18n.language !== next) {
      await i18n.changeLanguage(next);
    }
    if (next) {
      document.documentElement.lang = next;
    }
  } catch {
  }
}

bootstrap();
